import { useState } from "react";
import { Button } from "@groot/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@groot/ui/card";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Badge } from "@groot/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Switch } from "@/app/shared/ui/switch";
import { Table, TableBody, TableCell, TableRow } from "@groot/ui/table";
import { useConfirm } from "@groot/ui/primitives";
import {
  useBeancountStatus,
  useResetBeancountState,
  useRunBeancountSync,
  useUpdateBeancountConfig,
  useBeancountConfig,
  usePreviewBeancountSync,
} from "@/hooks/api/useBeancountSync";
import type { BeancountSyncPlan, SyncOp } from "@/types";
import { formatDate } from "@/lib/date.utils";
import { ArrowDownUp, Eye, Play, RotateCcw, Save } from "lucide-react";

const ACTION_STYLES: Record<SyncOp["action"], { label: string; className: string }> = {
  "push-create": { label: "→ ledger", className: "bg-primary/10 text-primary" },
  "push-update": { label: "→ ledger", className: "bg-primary/10 text-primary" },
  "pull-update": { label: "→ app", className: "bg-chart-2/15 text-chart-2" },
  "app-create": { label: "→ app (new)", className: "bg-chart-2/15 text-chart-2" },
  "app-delete": { label: "→ app (delete)", className: "bg-destructive/10 text-destructive" },
  "ledger-delete": { label: "→ ledger (drop)", className: "bg-destructive/10 text-destructive" },
  conflict: { label: "conflict", className: "bg-destructive/15 text-destructive" },
  skip: { label: "skip", className: "bg-muted text-muted-foreground" },
};

function PlanTable({ plan }: { plan: BeancountSyncPlan }) {
  return (
    <div className="max-h-80 overflow-y-auto rounded-md border border-border">
      <Table>
        <TableBody>
          {plan.ops.map((op, i) => {
            const style = ACTION_STYLES[op.action];
            return (
              <TableRow key={`${op.action}-${op.entityType}-${op.entityId}-${i}`}>
                <TableCell className="px-3 py-1.5">
                  <Badge variant="secondary" className={style.className}>
                    {style.label}
                  </Badge>
                </TableCell>
                <TableCell className="px-2 py-1.5 text-muted-foreground">{op.entityType}</TableCell>
                <TableCell className="px-2 py-1.5 font-medium">{op.label}</TableCell>
                <TableCell className="px-3 py-1.5 text-muted-foreground">{op.detail}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function BeancountSyncSettings() {
  const { data: config } = useBeancountConfig();
  const { data: status, isLoading: statusLoading } = useBeancountStatus();
  const updateConfig = useUpdateBeancountConfig();
  const preview = usePreviewBeancountSync();
  const runSync = useRunBeancountSync();
  const resetState = useResetBeancountState();
  const confirm = useConfirm();

  const [ledgerKey, setLedgerKey] = useState<string | null>(null);
  const [plan, setPlan] = useState<BeancountSyncPlan | null>(null);
  const [gitUrl, setGitUrl] = useState<string | null>(null);
  const [gitBranch, setGitBranch] = useState<string | null>(null);

  const effectiveLedgerKey = ledgerKey ?? config?.ledgerKey ?? "";
  const effectiveGitUrl = gitUrl ?? config?.git?.remoteUrl ?? "";
  const effectiveGitBranch = gitBranch ?? config?.git?.branch ?? "main";

  const handleSave = () => {
    updateConfig.mutate({ ledgerKey: effectiveLedgerKey });
  };

  const handleSaveGit = () => {
    const url = effectiveGitUrl.trim();
    updateConfig.mutate({
      git: url ? { remoteUrl: url, branch: effectiveGitBranch.trim() || "main" } : null,
    });
  };

  const handlePreview = async () => {
    const result = await preview.mutateAsync(undefined);
    setPlan(result);
  };

  const handleSync = async () => {
    if (plan && plan.summary.conflicts > 0) {
      const ok = await confirm({
        title: "Confirm Action",
        description: `This sync will skip ${plan.summary.conflicts} conflicted item(s). Continue?`,
      });
      if (!ok) return;
    }
    const result = await runSync.mutateAsync(undefined);
    // /sync now enqueues the beancount-sync job (memory-heavy work runs in
    // the worker, not the web dyno) — only a dry-run plan comes back inline.
    if (!("queued" in result)) {
      setPlan(result);
    }
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: "Confirm Action",
      description:
        "Reset sync state? All entity↔directive links are dropped. The next sync will re-link from scratch (safe, but it cannot tell app-changes from ledger-changes until then).",
    });
    if (ok) {
      await resetState.mutateAsync();
      setPlan(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ArrowDownUp className="size-4" /> Two-way Beancount Sync
            </CardTitle>
            {config && (
              <div className="flex items-center gap-2">
                <Switch
                  id="beancount-enabled"
                  checked={config.enabled}
                  onCheckedChange={(checked) => updateConfig.mutate({ enabled: checked })}
                />
                <Label htmlFor="beancount-enabled" className="text-sm text-muted-foreground">
                  {config.enabled ? "Enabled" : "Disabled"}
                </Label>
              </div>
            )}
          </div>
          <CardDescription>
            Keep your categories, accounts, and transactions in sync with a beancount ledger file.
            App edits are pushed to the ledger; ledger edits are pulled back into the app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="ledger-key">Ledger file</Label>
              <div className="flex gap-2">
                <Input
                  id="ledger-key"
                  value={effectiveLedgerKey}
                  onChange={(e) => setLedgerKey(e.target.value)}
                  placeholder="beancount/ledger.beancount"
                />
                <Button variant="outline" size="icon" onClick={handleSave} title="Save">
                  <Save className="size-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Stored in app storage. Download it from the Storage page; anything you write outside
                the managed section is preserved.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="git-remote">Git mirror (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="git-remote"
                  value={effectiveGitUrl}
                  onChange={(e) => setGitUrl(e.target.value)}
                  placeholder="https://github.com/you/ledger.git"
                />
                <Input
                  aria-label="Git branch"
                  className="w-28"
                  value={effectiveGitBranch}
                  onChange={(e) => setGitBranch(e.target.value)}
                  placeholder="main"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleSaveGit}
                  title="Save git mirror"
                >
                  <Save className="size-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Two-way mirror of the ledger in a private git repo. Every sync pulls first (hand
                edits from any git client are imported) and pushes the regenerated ledger after.
                {config?.git ? (
                  config.git.tokenConfigured ? (
                    <Badge variant="secondary" className="ml-1">
                      token configured
                    </Badge>
                  ) : (
                    <span className="text-destructive">
                      {" "}
                      — set NF_GITHUB_PAT (repo-scoped PAT) on the server
                    </span>
                  )
                ) : null}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Delete policy</Label>
              <Select
                value={config?.deletePolicy ?? "app-first"}
                onValueChange={(v) =>
                  updateConfig.mutate({ deletePolicy: v as "app-first" | "ledger-first" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="app-first">App-first — recreate deleted directives</SelectItem>
                  <SelectItem value="ledger-first">
                    Ledger-first — delete app rows deleted in ledger
                  </SelectItem>
                </SelectContent>
              </Select>
              <Label>Conflict policy</Label>
              <Select
                value={config?.conflictPolicy ?? "skip"}
                onValueChange={(v) =>
                  updateConfig.mutate({ conflictPolicy: v as "skip" | "app" | "ledger" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip — leave both sides untouched</SelectItem>
                  <SelectItem value="app">App wins — overwrite ledger</SelectItem>
                  <SelectItem value="ledger">Ledger wins — overwrite app</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={!config?.enabled || preview.isPending}
            >
              <Eye className="size-4" /> {preview.isPending ? "Checking…" : "Preview changes"}
            </Button>
            <Button onClick={handleSync} disabled={!config?.enabled || runSync.isPending}>
              <Play className="size-4" /> {runSync.isPending ? "Syncing…" : "Sync now"}
            </Button>
            <Button variant="ghost" onClick={handleReset} disabled={resetState.isPending}>
              <RotateCcw className="size-4" /> Reset state
            </Button>
          </div>

          {!statusLoading && status && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>
                Last sync: {status.lastSyncedAt ? formatDate(status.lastSyncedAt) : "never"}
              </span>
              <span>· {status.mappedCounts.accounts} accounts</span>
              <span>· {status.mappedCounts.categories} categories</span>
              <span>· {status.mappedCounts.transactions} transactions mapped</span>
            </div>
          )}
        </CardContent>
      </Card>

      {plan && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {plan.dryRun ? "Planned changes (dry run)" : "Sync result"}
            </CardTitle>
            <CardDescription>
              {plan.ops.length === 0
                ? "Everything is already in sync."
                : `${plan.ops.length} operation(s)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plan.ops.length > 0 ? (
              <PlanTable plan={plan} />
            ) : (
              <p className="text-sm text-muted-foreground">Nothing to do.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
